"use client";
import { useState, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
import { getAccessToken } from "@/app/lib/cookie";
import { api_url } from "@/app/api";
import { METHOD_TYPE } from "@/app/lib/enums";

interface UseContentFromFileResult {
   isLoading: boolean;
   error: string | null;
   processFile: (file: File) => Promise<string>;
   processFiles: (files: File[] | FileList) => Promise<{ data: string; list: string[] }>;
}

export const useContentFromFile = (): UseContentFromFileResult => {
   useEffect(() => {
      if (typeof window !== "undefined") {
         pdfjsLib.GlobalWorkerOptions.workerSrc = "/js/pdf.worker.min.js";
      }
   }, []);

   const [isLoading, setIsLoading] = useState<boolean>(false);
   const [error, setError] = useState<string | null>(null);

   const fetchWithFormData = async (url: string, formData: FormData) => {
      const token = getAccessToken();
      if (!token) {
         throw new Error("No authentication token found");
      }

      return fetch(url, {
         method: METHOD_TYPE.POST,
         headers: {
            Authorization: token
         },
         body: formData
      });
   };

   const uploadImage = async (imageBlob: Blob): Promise<string> => {
      if (!imageBlob) return "";
      try {
         const formData = new FormData();
         const extension =
            imageBlob.type === "image/png"
               ? ".png"
               : imageBlob.type === "image/jpeg"
                 ? ".jpg"
                 : ".bin";
         const filename = `image_${Date.now()}${extension}`;
         const status = "inactive";
         formData.append("file", imageBlob, filename);
         formData.append("status", status);
         formData.append("visibility", "public");

         const response = await fetchWithFormData(api_url.upload_url, formData);

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
               errorData?.error || `Upload failed with status: ${response.status}`
            );
         }

         const result = await response.json();
         if (result?.status === "error") {
            throw new Error(result?.error || "Upload failed");
         }

         return result?.data?.url ?? "";
      } catch (err) {
         console.error("Error when uploading pictures:", err);
         throw err;
      }
   };

   const extractImagesFromPdf = async (
      arrayBuffer: ArrayBuffer
   ): Promise<{ url: string; position: number }[]> => {
      const uint8Array = new Uint8Array(arrayBuffer);
      const images: { url: string; position: number }[] = [];

      const pngSignature = [0x89, 0x50, 0x4e, 0x47];
      const jpegSignature = [0xff, 0xd8, 0xff];

      for (let i = 0; i < uint8Array.length - 4; i++) {
         if (pngSignature.every((byte, index) => uint8Array[i + index] === byte)) {
            let end = i;
            while (end < uint8Array.length - 8) {
               if (
                  uint8Array[end] === 0x49 &&
                  uint8Array[end + 1] === 0x45 &&
                  uint8Array[end + 2] === 0x4e &&
                  uint8Array[end + 3] === 0x44
               ) {
                  end += 8;
                  break;
               }
               end++;
            }
            const imageBlob = new Blob([uint8Array.slice(i, end)], {
               type: "image/png"
            });
            const uploadedUrl = await uploadImage(imageBlob);
            images.push({ url: uploadedUrl, position: i });
         }

         if (jpegSignature.every((byte, index) => uint8Array[i + index] === byte)) {
            let end = i;
            while (end < uint8Array.length - 2) {
               if (uint8Array[end] === 0xff && uint8Array[end + 1] === 0xd9) {
                  end += 2;
                  break;
               }
               end++;
            }
            const imageBlob = new Blob([uint8Array.slice(i, end)], {
               type: "image/jpeg"
            });
            const uploadedUrl = await uploadImage(imageBlob);
            images.push({ url: uploadedUrl, position: i });
         }
      }
      return images;
   };

   const extractOdtContent = async (arrayBuffer: ArrayBuffer): Promise<string> => {
      try {
         const JSZip = (await import("jszip")).default;
         const zip = await JSZip.loadAsync(arrayBuffer);
         const contentXml = await zip.file("content.xml")?.async("text");

         if (!contentXml) {
            throw new Error("Could not find content.xml in ODT file");
         }

         const parser = new DOMParser();
         const xmlDoc = parser.parseFromString(contentXml, "text/xml");

         const parserError = xmlDoc.querySelector("parsererror");
         if (parserError) {
            throw new Error("Failed to parse XML content from ODT file");
         }

         let textContent = xmlDoc.documentElement.textContent || "";
         textContent = textContent
            .replace(/\s+/g, " ")
            .replace(/\n\s+/g, "\n")
            .replace(/^\s+|\s+$/g, "")
            .trim();

         if (!textContent) {
            throw new Error("No text content found in ODT file");
         }

         return textContent;
      } catch (error) {
         console.error("Error extracting ODT content:", error);
         throw new Error(
            `Failed to extract content from ODT file: ${
               error instanceof Error ? error.message : "Unknown error"
            }`
         );
      }
   };

   const extractDocxContentWithImages = async (
      arrayBuffer: ArrayBuffer
   ): Promise<string> => {
      try {
         const mammothAny = mammoth as any;
         const htmlResult = await mammothAny.convertToHtml({
            arrayBuffer
         });
         let processedContent = htmlResult.value;
         const base64ImageRegex =
            /<img[^>]*src="data:image\/([^;]+);base64,([^"]+)"[^>]*>/g;
         let match;
         let imageCount = 0;

         while ((match = base64ImageRegex.exec(htmlResult.value)) !== null) {
            imageCount++;
            try {
               const [fullMatch, imageType, base64Data] = match;
               const binaryString = atob(base64Data);
               const bytes = new Uint8Array(binaryString.length);
               for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
               }

               const imageBlob = new Blob([bytes], {
                  type: `image/${imageType}`
               });

               const uploadedUrl = await uploadImage(imageBlob);

               if (uploadedUrl) {
                  processedContent = processedContent.replace(
                     fullMatch,
                     `<img src="${uploadedUrl}" alt="Extracted image">`
                  );
               }
            } catch (imageErr) {
               console.error(`Error processing base64 image ${imageCount}:`, imageErr);
            }
         }

         let content = processedContent;
         content = content.replace(
            /<img[^>]*src="([^"]*)"[^>]*>/g,
            (_match: string, src: string) => {
               return ` ![image](${src})\n`;
            }
         );
         content = content
            .replace(/<p>/g, "\n")
            .replace(/<\/p>/g, "")
            .replace(/<br\s*\/?>/g, "\n")
            .replace(/<[^>]*>/g, "")
            .replace(/\n\s*\n/g, "\n\n")
            .trim();
         return content;
      } catch (err) {
         console.error("Error extracting DOCX content with positioned images:", err);
         const result = await mammoth.extractRawText({ arrayBuffer });
         return result.value;
      }
   };
   const _processFile = async (file: File): Promise<string> => {
      try {
         const fileExtension = file.name.split(".").pop()?.toLowerCase();
         const arrayBuffer = await file.arrayBuffer();
         let extractedContent = "";

         switch (fileExtension) {
            case "pdf":
               const pdfImages = await extractImagesFromPdf(arrayBuffer);
               pdfImages.sort((a, b) => a.position - b.position);

               const pdfDoc = await pdfjsLib.getDocument(arrayBuffer).promise;
               const numPages = pdfDoc.numPages;
               let pdfContent = "";

               for (let i = 1; i <= numPages; i++) {
                  const page = await pdfDoc.getPage(i);
                  const textContent = await page.getTextContent();
                  const pageText = textContent.items
                     .map((item: any) => item.str)
                     .join(" ");
                  pdfContent += pageText;

                  if (pdfImages.length > 0 && i < numPages) {
                     const imageUrl = pdfImages.shift()?.url;
                     if (imageUrl) {
                        pdfContent += ` ![image](${imageUrl})\n`;
                     }
                  }
               }
               if (pdfImages.length > 0) {
                  pdfContent +=
                     " " + pdfImages.map((img) => `![image](${img.url})`).join("\n");
               }

               extractedContent = pdfContent;
               break;

            case "docx":
               extractedContent = await extractDocxContentWithImages(arrayBuffer);
               break;

            case "txt":
            case "md":
               const decoder = new TextDecoder("utf-8");
               extractedContent = decoder.decode(arrayBuffer);
               break;

            case "odt":
               try {
                  const text = await extractOdtContent(arrayBuffer);
                  extractedContent = text;
               } catch (odtError) {
                  console.warn(
                     "Failed to parse ODT structure, falling back to text extraction"
                  );

                  const decoder = new TextDecoder("utf-8");
                  extractedContent = decoder.decode(arrayBuffer);
               }
               break;

            default:
               throw new Error("File format is not supported");
         }

         return extractedContent;
      } catch (err: any) {
         throw err;
      }
   };
   const processFile = async (file: File): Promise<string> => {
      setIsLoading(true);
      setError(null);

      try {
         const content = await _processFile(file);
         return content;
      } catch (err: any) {
         setError(err.message || "Error occurs when processing file");
         console.error("Error when processing file:", err);
         return "";
      } finally {
         setIsLoading(false);
      }
   };
   const processFiles = async (
      files: File[] | FileList
   ): Promise<{ data: string; list: string[] }> => {
      if (!files.length) return { data: "", list: [] };

      setIsLoading(true);
      setError(null);

      try {
         const fileContents: string[] = [];

         for (const file of files) {
            try {
               const content = await _processFile(file);
               fileContents.push(`\n\n${content}`);
            } catch (err: any) {
               console.error(`Error processing file ${file.name}:`, err);
               fileContents.push(
                  `# ${file.name}\n\n*Error: ${err.message || "Failed to process file"}*`
               );
            }
         }

         return { data: fileContents.join("\n"), list: fileContents };
      } catch (err: any) {
         setError(err.message || "Error occurs when processing files");
         console.error("Error when processing files:", err);
         return { data: "", list: [] };
      } finally {
         setIsLoading(false);
      }
   };
   return {
      isLoading,
      error,
      processFile,
      processFiles
   };
};
